/* eslint-disable no-param-reassign,prefer-const */
/* global React */
import { useEffect, useState } from 'react'
import { FormHelperText, Grid } from '@mui/material'
import { classNames, isObj, PlatformEvent } from 'lib/util'
import { useMemoRef, useStyles } from '@platform/react/hook'
import ScrollGrid from 'ui/Element/Grid/Scrollable'
import DynamicItem from 'ui/Element/Form/Dynamic/Item'
import DecoratedText from 'ui/Element/Text/Decorated'
import ErrorBoundary from 'ui/Error'

const styles = theme => ({
  fullHeight: {
    height: '100%',
  },
  border: {
    borderWidth: '0.0625rem',
    borderStyle: 'solid',
    borderColor: theme.palette.gray[900],
    borderRadius: '0.375rem',
  },
  start: {
    justifyContent: 'flex-start',
    alignContent: 'flex-start',
    alignItems: 'flex-start',
  },
})

/**
 * @typedef {Object<string, DynamicItemConfig>} DynamicFormState
 *
 */

/**
 * @typedef {Object} DynamicItemEffect
 *
 * @description Configuration object used to trigger changes to the properties of a configured
 *              item depending on the current state of any item in the same DynamicForm.
 *
 * @example
 * {
 *   "key": "amount",
 *   "type": "decimal",
 *   "label": "Amount",
 *   "hidden": true,
 *   "effects": [
 *     {
 *       "if": {
 *         "all": {
 *           "check1": { "value": true }, // "check1" is another item's key
 *           "check2": { "value": true } // "check2" is another item's key
 *         }
 *       },
 *       "apply": { "hidden": false }
 *     }
 *   ]
 * }
 *
 * Would apply `{ "hidden": false }` to "amount", if the value of both "check1" and "check2" items
 * is `true`.
 *
 * @property {DynamicFormState} if        an object containing "all", "any" and/or "none"
 *                                        conditions, which contain properties whose name is the key
 *                                        of other elements and whose value is an object containing
 *                                        properties of that element
 * @property {DynamicFormState} [if.all]  all properties of all elements declared inside this object
 *                                        must match the corresponding elements' state for the
 *                                        properties in the `apply` object to be applied
 * @property {DynamicFormState} [if.some] all properties of at least one of the elements declared
 *                                        inside this object must match the corresponding element's
 *                                        state for the properties in the `apply` object to be
 *                                        applied
 * @property {DynamicFormState} [if.none] no properties of the elements declared inside this object
 *                                        must match the corresponding element's state for the
 *                                        properties in the `apply` object to be applied
 * @property {DynamicItemConfig} apply    properties applied to the configured element if all
 *                                        conditions are fulfilled
 */

/**
 * @typedef {Object} DynamicItemConfig
 *
 * @description Configuration object used to specify the appearance and behavior of a
 *              dynamically-rendered item.
 *
 * @property {string} key                     item identifier, defining the item's key in the
 *                                            {@link DynamicForm}'s resulting value; enables
 *                                            validation and interaction through effects
 * @property {keyof DynamicItemTypes} type    type of rendered element
 * @property {DynamicItemEffect[]} [effects]  definition of external conditions that dynamically
 *                                            alter the properties of the item itself
 * @property {number} [size]                  space taken by the item
 * @property {boolean} [hidden]               whether the item isn't displayed
 */

/**
 * @typedef {DynamicItemConfig} SectionConfig
 * @property {DynamicItemConfig[]} items  list of elements to be contained by the section
 */

/**
 * @typedef {DynamicItemConfig} SpaceConfig
 */

/**
 * @typedef {DynamicItemConfig} ParagraphConfig
 * @property {string} text  Markdown text to display
 */

/**
 * @typedef {DynamicItemConfig} DynamicFieldConfig
 *
 * @description Configuration object used to specify the appearance and behavior of a
 *              dynamically-rendered field.
 *
 * @property {string} [label]                 text displayed above the field
 * @property {string} [helperText]            text displayed below the field if `error` is false
 * @property {any} [defaultValue]             the field's default value
 * @property {boolean} [disabled]             whether the field does not allow interaction
 * @property {boolean} [required]             whether the field must have a value for it to be valid
 * @property {Object|string} [requiredError]  text displayed as `helperText` when the `required`
 *                                            requirement is not met
 * @property {boolean} [error]                whether the field's value didn't pass its requirements
 * @property {Object|string} [errorText]      text displayed in place of `helperText`
 */

/**
 * @typedef {DynamicFieldConfig} TextFieldConfig
 * @property {string} [placeholder]           hint to display when there is no value
 * @property {boolean} [multiline]            whether the field takes more than one line of text
 * @property {number} [minRows]               minimum amount of text lines displayed by the field
 * @property {number} [maxRows]               maximum amount of text lines displayed by the field
 * @property {string} [regex]                 regular expression used to validate the field's value
 * @property {number} [minLength]             the minimum number of characters the value should have
 * @property {number} [maxLength]             the maximum number of characters the value should have
 * @property {Object|string} [regexError]     text to display when `regex` is not met
 * @property {Object|string} [minLengthError] text to display when `minLength` is not met
 * @property {Object|string} [maxLengthError] text to display when `maxLength` is not met
 */

/**
 * @typedef {DynamicFieldConfig} DateFieldConfig
 * @property {string} [placeholder]           hint to display when there is no value
 * @property {boolean} [fromToday]            if only dates from today onwards can be selected
 * @property {boolean} [untilToday]           if only dates up to today can be selected
 * @property {Object} [mask]                  custom mask, e.g. __/__/____ __:__
 * @property {Object} [format]                date format string, e.g. dd/MM/yyyy
 * @property {boolean} [defaultToday]         whether the field should start with today's date set
 * @property {string} [minDate]               the minimum date allowed to be selected
 * @property {string} [maxDate]               the maximum date allowed to be selected
 * @property {string} [rangeError]            text to display if `minDate` or `maxDate` are not met
 * @property {string} [invalidError]          text to display if the date is invalid
 */

/**
 * @typedef {DynamicFieldConfig} TimeFieldConfig
 * @property {string} [placeholder]           hint to display when there is no value
 * @property {boolean} [twentyFourHours]      whether the time should be displayed in 24h format
 * @property {boolean} [defaultNow]           whether the field starts with the current time as its
 *                                            value
 */

/**
 * @typedef {DynamicFieldConfig} SelectFieldConfig
 * @property {Object[]} [options]             items used by the radio/select field
 */

/**
 * @typedef {DynamicFieldConfig} RadioGroupFieldConfig
 * @property {Object[]} [options]             items used by the radio/select field
 */

/**
 * @typedef {DynamicFieldConfig} NumberFieldConfig
 * @property {string} [placeholder]           hint to display when there is no value
 * @property {number} [min]                   the minimum value
 * @property {number} [max]                   the maximum value
 * @property {number} [step]                  amount incremented/decre
 * @property {string} [unitStart]
 * @property {string} [unitEnd]
 * @property {Object|string} [minError]
 * @property {Object|string} [maxError]
 * @property {Object|string} [rangeError]
 */

/**
 * @typedef {DynamicFieldConfig} CheckFieldConfig
 * @property {boolean} [checked]
 * @property {any} [checkedValue]
 * @property {any} [uncheckedValue]
 * @property {Object|string} [subLabel]
 * @property {boolean} [labelStart]
 * @property {boolean} [inline]               whether the element should take its whole parent's
 *                                            width
 */

/**
 * Returns a new object containing only those props from `items` that are used by their UI
 * representation.
 *
 * @param {DynamicItemConfig[]} items
 * @returns {DynamicItemConfig[]}
 */
const withoutConfigProps = items => items.map((item) => {
  const {
    effects,
    requiredError,
    regex,
    regexError,
    minLength,
    minLengthError,
    maxLength,
    maxLengthError,
    minError,
    maxError,
    items: children,
    ...newItem
  } = item

  const newItems = children ? { items: withoutConfigProps(children) } : {}

  return { ...newItem, ...newItems }
})

/**
 * Filters out values of hidden and removed elements.
 *
 * @param {DynamicItemConfig[]} [items]   current items' configuration
 * @param {DynamicItemValue} [values]     current items' values
 * @param {{ value: boolean }} [changed]  internal change monitor
 * @returns {DynamicItemValue}
 */
const filterValues = (items = [], values = {}, changed = { value: false }) => {
  const filteredValues = Object
    .keys(values)
    .reduce((acc, key) => {
      const value = values[key]
      const item = items.find(i => i.key === key)

      if ((value || value === 0) && (!item || item.hidden)) {
        acc[key] = null
      } else if (value && isObj(value) && item?.items) {
        acc[key] = filterValues(item.items, value, changed)
      } else {
        acc[key] = value
      }

      changed.value ||= acc[key] !== value

      return acc
    }, {})

  return changed.value ? filteredValues : values
}

/**
 * Returns `items` if they all have a unique `key` property, otherwise throws an error.
 *
 * @param {DynamicItemConfig[]} items
 * @param {string[]} [keys]
 * @returns {*}
 */
const assertKeys = (items, keys = []) => items.map((item, index) => {
  if (!item.key) {
    throw TypeError(`Missing "key" for element at position: ${index}.`)
  }

  if (keys.includes(item.key)) {
    throw TypeError(`Key "${item.key}" is not unique.`)
  }

  keys.push(item.key)

  if (item.items) {
    try {
      item.items = assertKeys(item.items, keys)
    } catch (error) {
      throw new TypeError(`"Element ${item.key}.items": ${error.message}.`)
    }
  }

  return item
})

/**
 * Returns `items` if they are valid or throws an error if they are not.
 *
 * @param {DynamicItemConfig[]} items
 * @returns {DynamicItemConfig[]}
 */
const asserted = (items) => {
  if (!items.length) {
    throw TypeError('No items found')
  }

  return assertKeys(items)
}

/**
 * Displays either `error.message` or `value` in a {@link DecoratedText}
 *
 * @param {Error} error   error object containing the message to display
 * @param {string} value  value to display, if `error` has no message
 * @returns {JSX.Element}
 */
const InvalidConfigurationMessage = ({ error, value = 'No valid configuration' }) => (
  <Grid
    item
    xs={12}
    padding={3}
  >
    <DecoratedText
      decorators={{ error: true }}
      value={error?.message || value}
    />
  </Grid>
)

/**
 * Returns an array of {@link DynamicItem} elements.
 *
 * @param {DynamicItemConfig[]} items the configuration objects defining each {@link DynamicItem}
 * @param {DynamicItemValue} value     the `items` values
 * @param {Object} events               event handlers used by {@link DynamicItem}
 * @returns {DynamicItem[]}
 */
const DynamicItems = ({ items, value, events }) => withoutConfigProps(asserted(items))
  .filter(item => !item.hidden)
  .map((item, index) => (
    <DynamicItem
      key={item.key || index}
      item={item}
      value={value[item.key]}
      events={events}
    />
  ))

/**
 * @typedef {Object<string, any>} DynamicItemValue
 */

/**
 * DynamicForm component
 *
 * @param {Object} forwardedRef                       a forwarded reference to the root element
 * @param {Object} events                             component's event handlers
 * @param {function} events.onChange                  called whenever the component value changes
 * @param {function} events.onFormChange              called whenever the component's item
 *                                                    configuration changes
 * @param {Object} classes                            the component's style classes
 * @param {string} helperText                         text displayed below the component
 * @param {Object} props                              additional component's properties
 * @param {DynamicItemValue} [props.value]            an object containing a property key: value for
 *                                                    each item
 * @param {DynamicItemConfig[]} [props.configuration] component's items base configuration
 * @param {DynamicItemConfig[]} [props.items]         component's items display configuration
 * @param {boolean} props.border                      toggles component border display
 * @param {boolean} props.error                       whether the component is in error state
 * @param {string} props.errorText                    text shown on error instead of `helperText`
 * @returns {JSX.Element}                             the new component
 */
const DynamicForm = ({ forwardedRef, events = {}, helperText, ...props }) => {
  const classes = useStyles(styles)()
  const [configuration, setConfiguration] = useState(props.configuration || [])
  const [items, setItems] = useState(props.items || [])
  const [value, setValue] = useState(props.value || /** @type DynamicItemValue */ {})

  // handle changes of inner items
  const handleChange = event => setValue((prevValue) => {
    const { item: { key, hidden }, value: itemValue } = event.detail

    // TODO: We should remove the property (representing an item value) if its value is `null`,
    //  which means that the item was removed or its key changed, but the server doesn't allow us
    //  to remove properties from objects, so we leave them as `null` instead.
    // let newState
    // if (itemValue === null) {
    //   const { [key]: item, ...rest } = prevState
    //   newState = rest
    // } else {
    // const filteredValues = filterValues(items, prevValue)
    const newValue = { ...prevValue, [key]: hidden ? null : itemValue }
    // }

    events.onChange?.(new PlatformEvent(event, { value: newValue }))
    return newValue
  })

  // remove values of hidden/removed items after computing display configuration
  useEffect(() => {
    setValue(filterValues(items, value))
  }, [items])

  // compute items display configuration from item effects
  useEffect(() => {
    (async () => {
      const newItems = await events.onFormChange?.(new PlatformEvent('change', {
        configuration,
        value,
      })) || []

      setItems(newItems)
    })()
  }, [configuration, value])

  // set value from props
  useEffect(() => {
    const hasItems = !!items.length
    const newValue = hasItems ? filterValues(items, props.value || {}) : props.value
    setValue(newValue)
  }, [props.value])

  // set items display configuration (e.g. after external validation)
  useEffect(() => {
    setItems(props.items || items)
  }, [props.items])

  // set configuration from props
  useEffect(() => {
    setConfiguration(props.configuration || [])
  }, [props.configuration])

  return (
    <Grid
      ref={forwardedRef}
      item
      container
      className={classes.fullHeight}
      xs={12}
    >
      <Grid
        item
        container
        className={classNames(
          classes.fullHeight,
          props.border && classes.border,
        )}
        xs={12}
      >
        {!items?.length
          ? <InvalidConfigurationMessage value={'No items found'}/>
          : <ErrorBoundary fallback={error => <InvalidConfigurationMessage error={error}/>}>
            <ScrollGrid
              item
              container
              contentProps={{
                padding: 3,
                columnSpacing: 3,
              }}
              classes={{
                root: classes.fullHeight,
                content: classes.start,
              }}
              xs={12}
            >
              <DynamicItems
                items={items}
                value={value}
                events={{
                  onChange: handleChange,
                }}
              />
            </ScrollGrid>
          </ErrorBoundary>}
      </Grid>
      {helperText && (
        <Grid
          item
          component={FormHelperText}
          className={classes.helperText}
          marginTop={1}
          error={true}
        >
          {helperText}
        </Grid>
      )}
    </Grid>
  )
}

export default useMemoRef(DynamicForm, props => [
  props.configuration, props.items, props.value, props.error,
])
